import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, X, Loader2 } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function InboxPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API}/inbox/messages`, { headers: getAuthHeader() });
      setMessages(Array.isArray(r.data?.messages) ? r.data.messages : []);
      setUnread(Number(r.data?.unread_count || 0));
    } catch (e) {
      // Silent — endpoint may be unavailable
    }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 5 * 60 * 1000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [load]);

  const markRead = async (messageId) => {
    try {
      await axios.post(`${API}/inbox/mark-read`, { message_id: messageId }, { headers: getAuthHeader() });
      setMessages(prev => prev.map(m => m._id === messageId ? { ...m, is_read: true } : m));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) {
      // ignore
    }
  };

  const handleOpenMessage = (m) => {
    if (!m.is_read) markRead(m._id);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative w-9 h-9 flex items-center justify-center rounded-md hover:bg-[#1A2B42] transition-colors"
        data-testid="inbox-bell-btn"
        aria-label="Posteingang"
      >
        <Bell className="w-5 h-5 text-[#5A6C7D]" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="inbox-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="inbox-panel">
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="w-full max-w-md bg-[#0A1628] border-l border-[#1A2B42] shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#1A2B42] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#00D9C8]" />
                <h2 className="text-white font-semibold">Posteingang</h2>
                {unread > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
                    {unread} neu
                  </span>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#5A6C7D] hover:text-white transition-colors"
                data-testid="inbox-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" />
                </div>
              ) : messages.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-[#5A6C7D]" data-testid="inbox-empty">
                  Keine Nachrichten vorhanden
                </div>
              ) : (
                <ul className="divide-y divide-[#1A2B42]">
                  {messages.map(m => (
                    <li
                      key={m._id}
                      onClick={() => handleOpenMessage(m)}
                      className={`px-5 py-4 cursor-pointer transition-colors hover:bg-[#1A2B42]/50 ${m.is_read ? '' : 'bg-[#1A2B42]/30'}`}
                      data-testid={`inbox-message-${m._id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`text-sm ${m.is_read ? 'text-[#9CA3B5] font-medium' : 'text-white font-semibold'}`}>
                          {!m.is_read && <span className="inline-block w-2 h-2 rounded-full bg-[#00D9C8] mr-2 align-middle" />}
                          {m.subject}
                        </h3>
                        <span className="text-xs text-[#5A6C7D] whitespace-nowrap">{formatDate(m.created_at)}</span>
                      </div>
                      <p className="text-sm text-[#9CA3B5] whitespace-pre-wrap leading-relaxed">{m.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
